import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  GetTokensFromRefreshTokenCommand,
} from "@aws-sdk/client-cognito-identity-provider";

import {
  createSrpSession,
  createSecretHash,
  wrapInitiateAuth,
  signSrpSession,
  wrapAuthChallenge,
} from "cognito-srp-helper";


export async function srpLogin({
  region,
  userPoolId,
  clientId,
  clientSecret,
  username,
  password,
}) {
  try {
    const client = new CognitoIdentityProviderClient({ region });

    const secretHash =
      clientSecret && clientSecret.length
        ? createSecretHash(username, clientId, clientSecret)
        : undefined;

    const srpSession = createSrpSession(username, password, userPoolId, false);

    const initiateReq = wrapInitiateAuth(srpSession, {
      ClientId: clientId,
      AuthFlow: "USER_SRP_AUTH",
      AuthParameters: {
        USERNAME: username,
        ...(secretHash && { SECRET_HASH: secretHash }),
      },
    });

    const initiateRes = await client.send(
      new InitiateAuthCommand(initiateReq)
    );

    const signedSession = signSrpSession(srpSession, initiateRes);

    const challengeReq = wrapAuthChallenge(signedSession, {
      ClientId: clientId,
      ChallengeName: "PASSWORD_VERIFIER",
      ChallengeResponses: {
        USERNAME: username,
        ...(secretHash && { SECRET_HASH: secretHash }),
      },
      Session: initiateRes.Session,
    });

    const challengeRes = await client.send(
      new RespondToAuthChallengeCommand(challengeReq)
    );

    const result = challengeRes.AuthenticationResult;
    if (!result) {
      throw new Error("Authentication failed – no tokens returned");
    }

    return {
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken,
      expiresIn: result.ExpiresIn,
      tokenType: result.TokenType,
    };
  } catch (error) {
    // Handle specific AWS Cognito errors
    if (error.name === 'UserNotFoundException') {
      throw new Error('User not found. Please check your username and try again.');
    }
    if (error.name === 'NotAuthorizedException') {
      throw new Error('Invalid username or password.');
    }
    if (error.name === 'InvalidParameterException') {
      throw new Error('Invalid login parameters. Please try again.');
    }
    if (error.name === 'TooManyRequestsException') {
      throw new Error('Too many login attempts. Please wait and try again later.');
    }
    if (error.name === 'UserNotConfirmedException') {
      throw new Error('Account not confirmed. Please check your email for verification instructions.');
    }

    // Re-throw other errors with a generic message
    throw new Error(`Authentication failed: ${error.message || 'Unknown error'}`);
  }
}

export async function refreshTokens({
  region,
  clientId,
  refreshToken,
  clientSecret,   // optional, if your app client uses a secret
  deviceKey,      // optional, if you're using remembered devices
}) {
  const client = new CognitoIdentityProviderClient({ region });

  const res = await client.send(
    new GetTokensFromRefreshTokenCommand({
      ClientId: clientId,
      RefreshToken: refreshToken,
      ...(clientSecret && { ClientSecret: clientSecret }),
      ...(deviceKey && { DeviceKey: deviceKey }),
    })
  );

  const result = res.AuthenticationResult;
  if (!result) {
    throw new Error("Token refresh failed – no tokens returned");
  }


  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    // If Cognito doesn't send a new refresh token, keep the original one
    refreshToken: result.RefreshToken ?? refreshToken,
    expiresIn: result.ExpiresIn,
    tokenType: result.TokenType,
  };
}

/**
 * Get user details from Cognito using an access token.
 */
export async function getUserDetails({ region, accessToken }) {
  const client = new CognitoIdentityProviderClient({ region });

  const res = await client.send(
    new GetUserCommand({
      AccessToken: accessToken,
    })
  );

  const attributes =
    res.UserAttributes?.reduce((acc, attr) => {
      acc[attr.Name] = attr.Value;
      return acc;
    }, {}) ?? {};

  return {
    username: res.Username,
    attributes,               // { email, email_verified, sub, ... }
    mfaOptions: res.MFAOptions,
    preferredMfaSetting: res.PreferredMfaSetting,
    userMFASettingList: res.UserMFASettingList,
    raw: res,
  };
}
